import { z } from 'zod';
import {
  EHR_PATIENT_FIELD,
  HttpError,
  US_STATE_PATTERN,
  US_ZIP_PATTERN,
  incompleteAddressFields,
  existingPatientConflictSchema,
  possibleDuplicateConflictSchema,
  type ConflictPatient,
  type CreatePatientInEhrRequest,
  type EhrPatientField,
  type PossibleMatch,
} from '@vagusx/clients';

// Mirrors the server's createPatientInEhrSchema so most mistakes are caught before
// the EHR is asked; the server re-validates everything.
export const formSchema = z
  .object({
    firstName: z.string().trim().min(1, 'First name is required'),
    lastName: z.string().trim().min(1, 'Last name is required'),
    dateOfBirth: z.string().min(1, 'Date of birth is required'),
    // Only the two values athena's `sex` can hold; anything else would not reach
    // the EHR.
    gender: z.enum(['female', 'male']).optional(),
    locationId: z.string().min(1, 'Location is required'),
    phone: z.string().trim(),
    email: z.union([z.literal(''), z.string().trim().email('Invalid email')]),
    address1: z.string().trim(),
    address2: z.string().trim(),
    city: z.string().trim(),
    state: z.union([z.literal(''), z.string().trim().regex(US_STATE_PATTERN, 'Two letters')]),
    zip: z.union([z.literal(''), z.string().trim().regex(US_ZIP_PATTERN, 'Invalid ZIP')]),
  })
  .superRefine((v, ctx) => {
    for (const field of incompleteAddressFields(v)) {
      ctx.addIssue({ code: 'custom', path: [field], message: 'Required' });
    }
    // The EHR refuses a patient it cannot reach or locate.
    const anyAddress = Boolean(v.address1 || v.address2 || v.city || v.state || v.zip);
    if (!v.phone && !v.email && !anyAddress) {
      ctx.addIssue({
        code: 'custom',
        path: ['phone'],
        message: 'Add a phone, email, or address',
      });
    }
  });

export type FormValues = z.infer<typeof formSchema>;

// `satisfies`: every standard field an EHR refusal can name, except the department,
// must be a form field, so renaming either side is a compile error.
export const EMPTY: FormValues = {
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  gender: undefined,
  locationId: '',
  phone: '',
  email: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
} satisfies FormValues &
  Record<Exclude<EhrPatientField, typeof EHR_PATIENT_FIELD.departmentId>, unknown>;

/** The fields the server's duplicate check looks up; editing one invalidates its verdict. */
const LOOKUP_FIELDS: ReadonlySet<string> = new Set([
  'firstName',
  'lastName',
  'dateOfBirth',
  'phone',
]);

export function isLookupField(name: string): boolean {
  return LOOKUP_FIELDS.has(name);
}

/** What the server's duplicate check found, shown in place of a toast. */
export type Conflict =
  { kind: 'existing'; patient: ConflictPatient } | { kind: 'possible'; matches: PossibleMatch[] };

const blankToUndefined = (v: string): string | undefined => (v ? v : undefined);

export function toRequest(
  values: FormValues,
  allowPossibleDuplicate: boolean
): CreatePatientInEhrRequest {
  const hasAddress = Boolean(values.address1);
  return {
    firstName: values.firstName,
    lastName: values.lastName,
    dateOfBirth: values.dateOfBirth,
    locationId: values.locationId,
    gender: values.gender,
    phone: blankToUndefined(values.phone),
    email: blankToUndefined(values.email),
    address1: hasAddress ? values.address1 : undefined,
    address2: hasAddress ? blankToUndefined(values.address2) : undefined,
    city: hasAddress ? values.city : undefined,
    state: hasAddress ? values.state.toUpperCase() : undefined,
    zip: hasAddress ? values.zip : undefined,
    allowPossibleDuplicate: allowPossibleDuplicate || undefined,
  };
}

/** Read the server's duplicate refusal, or null for any other failure. */
export function conflictFrom(err: unknown): Conflict | null {
  if (!(err instanceof HttpError) || !err.isConflict) return null;
  if (err.code === 'ALREADY_EXISTS') {
    const parsed = existingPatientConflictSchema.safeParse(err.details);
    return parsed.success ? { kind: 'existing', patient: parsed.data.existingPatient } : null;
  }
  if (err.code === 'POSSIBLE_DUPLICATE') {
    const parsed = possibleDuplicateConflictSchema.safeParse(err.details);
    return parsed.success ? { kind: 'possible', matches: parsed.data.possibleMatches } : null;
  }
  return null;
}

/**
 * The form input a server field error belongs to, or null when there is none.
 * Takes the server's own names (`contact` → Phone, where a missing contact is
 * shown) and the standard names an EHR refusal uses (ADR-282): every one but the
 * department names a form field of the same name (checked on {@link EMPTY}), and
 * the department is chosen here as a location.
 */
export function formFieldOf(field: string): keyof FormValues | null {
  if (field === 'contact') return 'phone';
  if (field === EHR_PATIENT_FIELD.departmentId) return 'locationId';
  return field in EMPTY ? (field as keyof FormValues) : null;
}
